import { Link } from 'react-router-dom'
import { ArrowRight, Users, Calendar, DollarSign, TrendingUp, BookOpen, UserPlus, Shield, Clock, Award, FileText, Settings } from 'lucide-react'
import SEO from '../../components/SEO'
import './Product.css'

const modules = [
  {
    icon: UserPlus,
    title: 'Recruitment & Onboarding',
    desc: 'Manage the entire hiring lifecycle from job requisition to offer letter. Publish openings, screen applicants, schedule interviews, and onboard new hires with structured workflows, document checklists, and e-signature support.',
    features: ['Job requisition & approval workflows', 'Multi-channel job posting', 'Applicant tracking & scoring', 'Interview scheduling & feedback', 'Digital offer letters & e-sign', 'Onboarding task management'],
  },
  {
    icon: Users,
    title: 'Employee Self-Service',
    desc: 'Empower employees to manage their own HR tasks. View payslips, apply for leave, update personal information, access company policies, and raise HR requests — all from a mobile-friendly portal.',
    features: ['Personal profile & document upload', 'Leave application & tracking', 'Payslip & tax document access', 'Expense claim submission', 'Policy library & acknowledgement', 'HR helpdesk ticketing'],
  },
  {
    icon: Calendar,
    title: 'Leave & Attendance',
    desc: 'Automate attendance capture through biometrics, mobile GPS, or web punch-in. Configure complex leave policies including earned leave, casual leave, sick leave, and comp-off with approval workflows.',
    features: ['Biometric & mobile attendance', 'GPS-based geo-fencing', 'Configurable leave types & accruals', 'Holiday calendar management', 'Shift scheduling & roster', 'Overtime & late-mark tracking'],
  },
  {
    icon: DollarSign,
    title: 'Payroll Processing',
    desc: 'Run accurate, on-time payroll for any number of employees. Handles gross-to-net calculations, statutory deductions (PF, ESI, TDS, PT), bonus, arrears, and generates compliant pay registers and Form 16.',
    features: ['Configurable salary structures', 'PF, ESI, TDS & PT automation', 'Variable pay & incentive processing', 'Arrears & revised pay calculation', 'Full & final settlement', 'Form 16 & 24Q generation'],
  },
  {
    icon: TrendingUp,
    title: 'Performance Management',
    desc: 'Drive a high-performance culture with structured goal-setting, continuous feedback, and periodic appraisals. Supports KRA/KPI-based and 360-degree review frameworks.',
    features: ['Goal & KRA setting (OKR/KPI)', '360-degree feedback', 'Continuous check-in journals', 'Mid-year & annual appraisal cycles', 'Rating normalization & calibration', 'Succession planning insights'],
  },
  {
    icon: BookOpen,
    title: 'Learning & Development',
    desc: 'Build a skilled workforce with an integrated LMS. Assign courses, track completions, manage certifications, and identify skill gaps through competency frameworks.',
    features: ['Course library & content upload', 'Learning path assignment', 'Certification & expiry tracking', 'Skills & competency matrix', 'Training need identification', 'Vendor & external training log'],
  },
  {
    icon: Clock,
    title: 'Time & Project Tracking',
    desc: 'Track employee time against projects, clients, and tasks. Useful for professional services, IT firms, and project-based businesses for accurate billing and productivity analysis.',
    features: ['Timesheet submission & approval', 'Project & task allocation', 'Client billing rate configuration', 'Productivity analytics', 'Integration with payroll', 'Manager dashboards'],
  },
  {
    icon: FileText,
    title: 'Statutory Compliance',
    desc: 'Stay audit-ready with automated generation of all statutory filings. Covers PF, ESI, PT, gratuity, labour law registers, and factory act compliance for multi-state operations.',
    features: ['PF ECR & ESI returns', 'Professional Tax state-wise', 'Gratuity & bonus calculation', 'Form II / Form V registers', 'Labour law compliance reports', 'Multi-state regulatory support'],
  },
  {
    icon: Award,
    title: 'Benefits & Compensation',
    desc: 'Design competitive benefit programmes including health insurance, flexible benefits, employee loans, advances, and reimbursements — all integrated with payroll.',
    features: ['Health & life insurance admin', 'Flexible benefit plan (FBP)', 'Loan & advance management', 'Reimbursement claims', 'CTC structuring tool', 'Benefits enrolment portal'],
  },
  {
    icon: Shield,
    title: 'Role-Based Access & Security',
    desc: 'Granular access control ensures every user sees only what they need to. Configure HR business partner, manager, and employee roles with field-level data permissions.',
    features: ['Role-based access control (RBAC)', 'Field-level data permissions', 'Audit trail & activity logs', 'SSO & MFA support', 'Data masking for sensitive fields', 'GDPR/DPDP compliance tools'],
  },
  {
    icon: Settings,
    title: 'Workflow & Approvals',
    desc: 'Build multi-level approval chains for any HR process — leave requests, expense claims, hiring approvals, and salary revisions — with configurable escalation rules.',
    features: ['Multi-level approval chains', 'Delegation & proxy approvals', 'Escalation & SLA reminders', 'Email & in-app notifications', 'Conditional routing logic', 'Mobile approval interface'],
  },
  {
    icon: TrendingUp,
    title: 'HR Analytics & Reports',
    desc: 'Make data-driven people decisions with pre-built dashboards and custom report builder. Headcount trends, attrition analysis, cost-per-hire, payroll analytics, and more.',
    features: ['Pre-built HR dashboards', 'Custom report builder', 'Headcount & attrition analytics', 'Payroll cost analytics', 'Diversity & inclusion metrics', 'Scheduled report delivery'],
  },
]

export default function HCM() {
  return (
    <>
      <SEO
        title="Human Capital Management System"
        description="NexCore HCM is a complete HR platform covering recruitment, payroll, attendance, performance, compliance, and more for Indian enterprises."
        keywords="HCM software India, HR management system, payroll software India, attendance management, PF ESI compliance software"
        path="/products/human-capital-management"
      />

      <section className="page-hero">
        <div className="container">
          <p className="section-label">Human Capital Management</p>
          <h1 className="section-title">Your workforce, fully managed</h1>
          <p className="section-subtitle">One platform for every HR process — from the moment you post a job to an employee's last working day. Built for Indian compliance, designed for modern teams.</p>
          <div className="page-hero__actions">
            <Link to="/contact" className="btn-primary">Request a Demo <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="product-overview">
        <div className="container">
          <div className="product-highlights">
            <div className="product-highlight"><strong>12</strong><span>Core Modules</span></div>
            <div className="product-highlight"><strong>100%</strong><span>Indian Compliance</span></div>
            <div className="product-highlight"><strong>Mobile</strong><span>First Design</span></div>
            <div className="product-highlight"><strong>API</strong><span>Integration Ready</span></div>
          </div>
        </div>
      </section>

      <section className="product-modules">
        <div className="container">
          <div className="product-modules__header">
            <p className="section-label">Modules</p>
            <h2 className="section-title">Everything HR in one place</h2>
            <p className="section-subtitle">Each module works standalone or as part of the full suite — deploy what you need, expand when you're ready.</p>
          </div>
          <div className="modules-grid">
            {modules.map(({ icon: Icon, title, desc, features }) => (
              <div key={title} className="module-card">
                <div className="module-card__header">
                  <div className="module-card__icon"><Icon size={22} /></div>
                  <h3>{title}</h3>
                </div>
                <p>{desc}</p>
                <ul className="module-card__features">
                  {features.map(f => <li key={f}><span className="check">✓</span>{f}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="product-cta">
        <div className="container">
          <h2>See NexCore HCM in action</h2>
          <p>Book a 30-minute personalized walkthrough with our HR product specialists.</p>
          <Link to="/contact" className="btn-primary">Schedule Demo <ArrowRight size={16} /></Link>
        </div>
      </section>
    </>
  )
}
